import { useEffect, useRef, useState } from "react";
import { useLenis } from "@/components/SmoothScrollProvider";

const LINKS = [
  { id: "warehouse", label: "Warehouse", testId: "nav-link-warehouse" },
  { id: "fabrics", label: "Fabrics", testId: "nav-link-fabrics" },
  { id: "about", label: "About", testId: "nav-link-about" },
  { id: "pricing", label: "Pricing", testId: "nav-link-pricing" },
  { id: "contact", label: "Contact", testId: "nav-link-contact" },
];

export const StickyNav = () => {
  const lenis = useLenis();
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const overlayRef = useRef(null);
  const closeBtnRef = useRef(null);
  const burgerRef = useRef(null);

  // Solid state once the hero has fully scrolled past (nav sits over content).
  useEffect(() => {
    let ticking = false;
    const check = () => {
      ticking = false;
      const hero = document.getElementById("hero");
      if (!hero) {
        setSolid(true);
        return;
      }
      const rect = hero.getBoundingClientRect();
      setSolid(rect.bottom <= 80);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(check);
      }
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Mobile overlay: focus trap + Escape close + scroll lock.
  useEffect(() => {
    if (!open) return undefined;
    const overlay = overlayRef.current;
    const previouslyFocused = document.activeElement;
    const getFocusables = () =>
      Array.from(overlay.querySelectorAll("a[href], button:not([disabled])"));

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const els = getFocusables();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (lenis) lenis.stop();
    closeBtnRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (lenis) lenis.start();
      if (previouslyFocused instanceof HrMLElement) previouslyFocused.focus();
      else burgerRef.current?.focus();
    };
  }, [open, lenis]);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const goTop = (e) => {
    e.preventDefault();
    setOpen(false);
    if (lenis) lenis.scrollTo(0, { duration: 1.4 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // The nav now sits on a translucent paper background at every scroll depth
  // (see the className below), so the text is always charcoal on paper.
  const linkColor = "text-charcoal";

  return (
    <>
      <nav
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,color,box-shadow,backdrop-filter] duration-[400ms] ease-out ${
          solid
            ? "bg-paper/90 backdrop-blur-[12px] [border-bottom:1px_dashed_#C4A87E]"
            : "bg-paper/70 backdrop-blur-[6px]"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-[72px] md:px-10">
          <a
            href="#hero"
            onClick={goTop}
            data-testid="nav-wordmark"
            aria-label="Spandex American Fabrics by Cherechian Trading Co, home"
            className={`flex flex-col leading-none ${linkColor} transition-colors duration-[400ms] ease-out`}
          >
            <span className="wordmark text-[13px] md:text-sm">Spandex American Fabrics</span>
            <span className="eyebrow mt-1 text-[12px] md:text-[13px]">
              Cherechian Trading Co
            </span>
          </a>

          <div className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={go(l.id)}
                data-testid={l.testId}
                className={`eyebrow nav-underline relative text-[13px] ${linkColor} transition-colors duration-[400ms] ease-out`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={go("contact")}
              data-testid="nav-request-samples-button"
              className={`eyebrow border border-kraft px-5 py-2.5 text-[13px] ${linkColor} transition-colors duration-300 ease-out hover:bg-kraft hover:text-ink`}
            >
              Contact us
            </a>
          </div>

          <button
            type="button"
            ref={burgerRef}
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className={`flex h-10 w-10 flex-col items-center justify-center gap-1.5 transition-colors duration-300 ease-out md:hidden ${linkColor}`}
          >
            <span className="block h-px w-6 bg-current" />
            <span className="block h-px w-6 bg-current" />
          </button>
        </div>
      </nav>

      {/* Mobile full-screen overlay */}
      <div
        ref={overlayRef}
        data-testid="nav-mobile-menu-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        className={`fixed inset-0 z-[60] flex flex-col bg-paper transition-opacity duration-300 ease-out ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="flex h-16 items-center justify-between px-5">
          <span className="flex flex-col leading-none text-charcoal">
            <span className="wordmark text-[13px]">Spandex American Fabrics</span>
            <span className="eyebrow mt-1 text-[12px]">Cherechian Trading Co</span>
          </span>
          <button
            type="button"
            ref={closeBtnRef}
            data-testid="nav-mobile-menu-close"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="flex h-10 w-10 items-center justify-center text-charcoal"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={go(l.id)}
              data-testid={`${l.testId}-mobile`}
              className="eyebrow text-lg text-charcoal transition-colors duration-300 ease-out hover:text-ember"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={go("contact")}
            data-testid="nav-request-samples-mobile"
            className="eyebrow mt-4 border border-kraft px-6 py-3 text-sm text-charcoal transition-colors duration-300 ease-out hover:bg-kraft hover:text-ink"
          >
            Contact us
          </a>
        </div>
      </div>
    </>
  );
};
